import { convertEmptyStringsToNull, convertIncomingPercentage, convertOutgoingPercentage } from "../../../pages/activityBuilder/modules/inputs/utils";
import { ProcessingEntryData, ProcessingEntryResponse } from "../../../pages/activityBuilder/modules/processing/processingTypes";
import { EntryCreatePayload, EntryDeletePayload, EntryUpdatePayload, ParentUpdatePayload, ParentResponse, ParentPayload } from "../../../types/modulesInterfaces";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createEntriesGetApi, createModuleGetApi } from "./factories/createModuleApi";

// Define constants for the module
const MODULE_TYPE = "Processing";
const TAG: TagTypes = "Processings";
const ENDPOINT = FEModulesObj[FEModules.Processing].path;

const ENTRY_MODULE_TYPE = "ProcessingEntries";
const TAG_ENTRY: TagTypes = "ProcessingEntries";
const TAG_ENTRY_DEFAULTS: TagTypes = "ProcessingEntriesDefaults";
export const ENTRY_ENDPOINT = "processing-entries";

const processingApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const transformEntriesResponse = (response: ProcessingEntryData[]) => {
	response.forEach(entry => convertIncomingPercentage(entry, ["transmission_loss_t2"]));
	return response.sort((a, b) => a.id - b.id);
};

const processingEntriesApiSlice = createEntriesGetApi<ProcessingEntryData[]>({
	apiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformEntriesResponse,
});

const publicProcessingApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const publicProcessingEntriesApiSlice = createEntriesGetApi<ProcessingEntryData[]>({
	apiSlice: publicApiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformEntriesResponse,
});

export const { useGetProcessingQuery } = processingApiSlice;
export const { useGetProcessingEntriesQuery, useGetProcessingEntriesDefaultsQuery } = processingEntriesApiSlice;

export const { useGetProcessingQuery: usePublicGetProcessingQuery } = publicProcessingApiSlice;
export const { 
	useGetProcessingEntriesQuery: usePublicGetProcessingEntriesQuery, 
	useGetProcessingEntriesDefaultsQuery: usePublicGetProcessingEntriesDefaultsQuery 
} = publicProcessingEntriesApiSlice;

export const mutationApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		updateProcessings: builder.mutation<ParentResponse, ParentUpdatePayload<ParentPayload>>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				convertEmptyStringsToNull(rest);
				return { url: `${ENDPOINT}/${id}/`, method: 'PUT', body: rest };
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: 'Processings', id: body?.id ?? 0 },
				{ type: "ModulesStatuses", id: activityId },
				{ type: 'ModuleResults', id: body?.id ? `processings-${body.id}` : undefined },
				{ type: 'ModuleHistory', id: body?.id ? `processings-${body.id}` : undefined },
			]
		}),
		updateProcessingEntry: builder.mutation<ProcessingEntryResponse, EntryUpdatePayload<ProcessingEntryResponse>>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				convertEmptyStringsToNull(rest);
				convertOutgoingPercentage(rest, ["transmission_loss_t2"]);
				return {
					url: `${ENTRY_ENDPOINT}/${id}/`,
					method: 'PUT',
					body: rest
				};
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: 'ProcessingEntries', id: activityId },
				{ type: 'ProcessingEntriesDefaults', id: body?.id ? body?.id : undefined },
			]
		}),
		createProcessingEntry: builder.mutation<ProcessingEntryResponse, EntryCreatePayload>({
			query: ({ activityId, parent }) => ({ 
				url: `${ENTRY_ENDPOINT}/`, 
				method: 'POST', 
				body: { activity: activityId, parent } 
			}),
			invalidatesTags: (_results, _error, { activityId, parent }) => [
				{ type: 'ProcessingEntries', id: activityId },
				{ type: 'ModuleResults', id: parent ? `processings-${parent}` : undefined },
				{ type: 'ModuleHistory', id: parent ? `processings-${parent}` : undefined },
				{ type: 'ModulesStatuses', id: activityId }
			]
		}),
		deleteProcessingEntry: builder.mutation<void, EntryDeletePayload>({
			query: ({ id }) => ({ url: `${ENTRY_ENDPOINT}/${id}/`, method: 'DELETE' }),
			invalidatesTags: (_results, _error, { activityId, parent, id }) => [
				{ type: 'ProcessingEntries', id: activityId },
				{ type: 'ModuleResults', id: parent ? `processings-${parent}` : undefined },
				{ type: 'ModuleHistory', id: parent ? `processings-${parent}` : undefined },
				{ type: 'ProcessingEntriesDefaults', id },
				{ type: 'ModulesStatuses', id: activityId }
			]
		}),
	}),
});

export const {
	useUpdateProcessingsMutation,
	useCreateProcessingEntryMutation,
	useUpdateProcessingEntryMutation,
	useDeleteProcessingEntryMutation,
} = mutationApiSlice;